import { notifyError, notifySuccess } from "@/lib/notification";
import * as educationService from "@/services/education.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const educationKey = {
  all: ["education"] as const,
  list: () => [...educationKey.all, "list"] as const,
  detail: (id: string) => [...educationKey.all, "detail", id] as const,
};

export const useEducationListQuery = () =>
  useQuery({
    queryKey: educationKey.list(),
    queryFn: educationService.fetchEducationList,
  });

export const useEducationQuery = (id: string) =>
  useQuery({
    queryKey: educationKey.detail(id),
    queryFn: () => educationService.getEducation(id),
    enabled: !!id,
  });

export const useAddEducationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<educationService.EducationType, AxiosError, educationService.EducationPayloadType>({
    mutationFn: educationService.addEducation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: educationKey.list() });
      notifySuccess("Education record added successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to add education record";
      notifyError(apiError);
    },
  });
};

export const useUpdateEducationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    educationService.EducationType,
    AxiosError,
    { id: string; data: Partial<educationService.EducationPayloadType> }
  >({
    mutationFn: ({ id, data }) => educationService.updateEducation(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: educationKey.list() });
      queryClient.invalidateQueries({ queryKey: educationKey.detail(id) });
      notifySuccess("Education record updated successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update education record";
      notifyError(apiError);
    },
  });
};

export const useDeleteEducationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: educationService.deleteEducation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: educationKey.list() });
      notifySuccess("Education record deleted successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to delete education record";
      notifyError(apiError);
    },
  });
};
